import React, { ErrorInfo, ReactNode } from 'react';
import { Box, Button, Container, Typography, Paper } from '@mui/material';
import { keyframes } from '@mui/system';

export interface ErrorBoundaryTheme {
  /**
   * Primary gradient colors for the background. Provide as [startColor, endColor].
   */
  gradientColors?: [string, string];
  /**
   * Icon to display. Can be an emoji or React node.
   */
  icon?: ReactNode;
  /**
   * Title to display when an error occurs.
   */
  title?: string;
  /**
   * Subtitle/description text.
   */
  subtitle?: string;
  /**
   * Additional message text.
   */
  message?: string;
  /**
   * Text for the retry/reset button.
   */
  retryButtonText?: string;
  /**
   * Text for the home button.
   */
  homeButtonText?: string;
  /**
   * Custom render function for the error display. If provided, overrides the default UI.
   */
  renderError?: (props: {
    error: Error | null;
    resetError: () => void;
    goHome: () => void;
  }) => ReactNode;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Theme configuration for customizing the error display.
   */
  theme?: ErrorBoundaryTheme;
  /**
   * Callback when an error is caught.
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /**
   * Whether to show error details. Defaults to showing only in development.
   */
  showErrorDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Animation keyframes
const pulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.1); }
`;

const wobble = keyframes`
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
`;

const defaultTheme: Required<Omit<ErrorBoundaryTheme, 'renderError'>> = {
  gradientColors: ['#667eea', '#764ba2'],
  icon: '⚠️',
  title: 'Something went wrong',
  subtitle: 'An unexpected error occurred',
  message: 'We apologize for the inconvenience. Please try refreshing the page or returning to the home page.',
  retryButtonText: 'Try Again',
  homeButtonText: 'Go Home',
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      const theme = { ...defaultTheme, ...this.props.theme };
      const showDetails = this.props.showErrorDetails ??
        (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development');

      // Use custom render function if provided
      if (this.props.theme?.renderError) {
        return this.props.theme.renderError({
          error: this.state.error,
          resetError: this.handleReset,
          goHome: this.handleGoHome,
        });
      }

      const [gradientStart, gradientEnd] = theme.gradientColors;

      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Animated background elements */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              pointerEvents: 'none',
            }}
          >
            {[...Array(5)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left: `${i * 25}%`,
                  top: '50%',
                  width: '2px',
                  height: '60%',
                  background: 'white',
                  transform: 'translateY(-50%)',
                  animation: `${pulse} ${2 + i * 0.3}s ease-in-out infinite`,
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            ))}
          </Box>

          <Container maxWidth="md">
            <Paper
              elevation={24}
              sx={{
                p: 6,
                textAlign: 'center',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 4,
                position: 'relative',
                overflow: 'visible',
              }}
            >
              {/* Icon display */}
              <Box
                sx={{
                  fontSize: '5rem',
                  mb: 3,
                  animation: `${wobble} 2s ease-in-out infinite`,
                }}
              >
                {theme.icon}
              </Box>

              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                }}
              >
                {theme.title}
              </Typography>

              <Typography
                variant="h5"
                color="text.secondary"
                gutterBottom
                sx={{ mb: 3, fontWeight: 500 }}
              >
                {theme.subtitle}
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
              >
                {theme.message}
              </Typography>

              {showDetails && this.state.error && (
                <Paper
                  sx={{
                    p: 2,
                    mb: 3,
                    background: 'rgba(255, 68, 68, 0.05)',
                    border: '1px solid rgba(255, 68, 68, 0.2)',
                    borderRadius: 2,
                    maxWidth: 600,
                    mx: 'auto',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      color: 'error.main',
                      textAlign: 'left',
                      wordBreak: 'break-word',
                    }}
                  >
                    <strong>Error Details:</strong>
                    <br />
                    {this.state.error.toString()}
                  </Typography>
                </Paper>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={this.handleReset}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
                    boxShadow: `0 4px 20px ${gradientStart}66`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${gradientStart}dd 0%, ${gradientEnd}dd 100%)`,
                      boxShadow: `0 6px 25px ${gradientStart}80`,
                    },
                  }}
                >
                  {theme.retryButtonText}
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={this.handleGoHome}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderColor: gradientStart,
                    color: gradientStart,
                    '&:hover': {
                      borderColor: gradientEnd,
                      background: `${gradientStart}0d`,
                    },
                  }}
                >
                  {theme.homeButtonText}
                </Button>
              </Box>

              {/* Decorative wave at bottom */}
              <Box
                sx={{
                  mt: 4,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1,
                  opacity: 0.3,
                }}
              >
                {[...Array(11)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 4,
                      height: `${10 + Math.abs(5 - i) * 5}px`,
                      background: `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
                      borderRadius: 2,
                      animation: `${pulse} ${1 + i * 0.1}s ease-in-out infinite`,
                    }}
                  />
                ))}
              </Box>
            </Paper>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
